import { useTracker } from '../lib/useTracker';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import { useTheme } from '../lib/useTheme';
import LocationCascade from '../components/locations/LocationCascade';
import { compressImage } from '../lib/compressImage';

const C = {
  turquesa: '#5dc1b9',
  mango: '#FDBE02',
  coral: '#eb636b',
  purple: '#534AB7',
  green: '#1D9E75',
  logoGrad: 'linear-gradient(135deg, #534AB7, #1D9E75)',
  dark: '#0a0a0a',
  gray: 'rgba(255,255,255,0.55)',
};

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";

const TOTAL_STEPS = 13;

const INTERESES = [
  { id:'marketing', emoji:'📊', label:'Marketing' },
  { id:'ventas', emoji:'💰', label:'Ventas' },
  { id:'branding', emoji:'🏷️', label:'Branding' },
  { id:'pricing', emoji:'🪙', label:'Pricing' },
  { id:'contenido', emoji:'📸', label:'Contenido' },
  { id:'comunidad', emoji:'👥', label:'Comunidad' },
  { id:'producto', emoji:'📦', label:'Producto' },
  { id:'diseno', emoji:'🎨', label:'Diseño' },
  { id:'ux', emoji:'📱', label:'UX' },
  { id:'tecnologia', emoji:'💻', label:'Tecnología' },
  { id:'ecommerce', emoji:'🛒', label:'E-commerce' },
  { id:'retail', emoji:'🏪', label:'Retail físico' },
  { id:'servicios', emoji:'🔧', label:'Servicios' },
  { id:'food', emoji:'🍜', label:'Food' },
  { id:'educacion', emoji:'🎓', label:'Educación' },
  { id:'salud', emoji:'❤️', label:'Salud' },
  { id:'sustentabilidad', emoji:'🌱', label:'Sustentabilidad' },
  { id:'arte', emoji:'🖌️', label:'Arte' },
  { id:'musica', emoji:'🎵', label:'Música' },
  { id:'fotografia', emoji:'📷', label:'Fotografía' },
  { id:'video', emoji:'🎬', label:'Video' },
  { id:'storytelling', emoji:'📖', label:'Storytelling' },
  { id:'liderazgo', emoji:'👑', label:'Liderazgo' },
  { id:'productividad', emoji:'✅', label:'Productividad' },
  { id:'mentalidad', emoji:'💡', label:'Mentalidad' },
  { id:'networking', emoji:'🔗', label:'Networking' },
  { id:'inversion', emoji:'📈', label:'Inversión' },
  { id:'crowdfunding', emoji:'🚀', label:'Crowdfunding' },
  { id:'internacional', emoji:'🌎', label:'Mercados internacionales' },
  { id:'mexico_profundo', emoji:'⛰️', label:'México profundo' },
];

const INDUSTRIES = [
  { label: 'Comida / bebidas', emoji: '🍽️', value: 'comida' },
  { label: 'Ropa / moda', emoji: '👗', value: 'ropa' },
  { label: 'Belleza / cosmética', emoji: '💅', value: 'belleza' },
  { label: 'Servicios profesionales', emoji: '💼', value: 'servicios' },
  { label: 'Tecnología / digital', emoji: '💻', value: 'tecnologia' },
  { label: 'Fitness / salud', emoji: '💪', value: 'fitness' },
  { label: 'Eventos / entretenimiento', emoji: '🎉', value: 'eventos' },
  { label: 'Educación / consultoría', emoji: '📚', value: 'educacion' },
  { label: 'Artesanías / handmade', emoji: '🎨', value: 'artesanias' },
  { label: 'Otro', emoji: '✨', value: 'otro' },
];

const STAGES = [
  { label: 'Solo tengo la idea', emoji: '💡', value: 'idea' },
  { label: 'Apenas arrancando', emoji: '🌱', value: 'arrancando' },
  { label: 'Validando (primeras ventas)', emoji: '🧪', value: 'validando' },
  { label: 'Estable pero no crece', emoji: '📊', value: 'estable' },
  { label: 'Creciendo', emoji: '📈', value: 'creciendo' },
  { label: 'Escalando', emoji: '🚀', value: 'escalando' },
];

const MODELS = [
  { label: 'Vendo productos físicos', emoji: '📦', value: 'producto_fisico' },
  { label: 'Vendo servicios', emoji: '🤝', value: 'servicio' },
  { label: 'Vendo productos digitales', emoji: '💻', value: 'producto_digital' },
  { label: 'Rento / alquilo', emoji: '🏠', value: 'renta' },
  { label: 'Todavía no vendo', emoji: '🤔', value: 'no_vendo' },
];

const SKILLS_OPTIONS = [
  { label: 'Ventas / negociación', value: 'ventas' },
  { label: 'Diseño / visual', value: 'diseno' },
  { label: 'Redes sociales', value: 'redes' },
  { label: 'Cocina / producción', value: 'produccion' },
  { label: 'Administración / finanzas', value: 'admin' },
  { label: 'Tecnología / web', value: 'tech' },
  { label: 'Atención al cliente', value: 'atencion' },
  { label: 'Creatividad / ideas', value: 'creatividad' },
];

const NEEDS_OPTIONS = [
  { label: 'Conseguir más clientes', value: 'clientes' },
  { label: 'Aprender a cobrar bien', value: 'pricing' },
  { label: 'Organizar mi negocio', value: 'organizacion' },
  { label: 'Crear contenido', value: 'contenido' },
  { label: 'Encontrar socios o equipo', value: 'equipo' },
  { label: 'Validar mi idea', value: 'validar' },
  { label: 'Automatizar procesos', value: 'automatizar' },
  { label: 'Motivación y acompañamiento', value: 'motivacion' },
];

export default function Onboarding() {
  const { isMobile, isTablet } = useScreen();
  const t = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = parseInt(searchParams.get('paso') || '0', 10);
  const setStep = (valOrFn) => {
    const newVal = typeof valOrFn === 'function' ? valOrFn(step) : valOrFn;
    setSearchParams({ paso: newVal.toString() }, { replace: false });
  };
  const [data, setData] = useState({
    full_name: '',
    location: { countryId: null, regionId: null, cityId: null },
    business_name: '',
    business_stage: '',
    business_type: '',
    sales_type: '',
    intereses: {},
    skills: {},
    needs: {},
    identidad: '',
    presentacion_descripcion: '',
    foto_proyecto: '',
    presentacion_quien: '',
    presentacion_que_hace: '',
    presentacion_que_busca: '',
    primerPensamiento_titulo: '',
    primerPensamiento_contenido: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const navigate = useNavigate();
  const { user, profile, onboardingDone, refreshProfile } = useAuth();
  useTracker("onboarding", user?.id);

  useEffect(() => {
    if (!user) return;
    if (onboardingDone) navigate('/sala', { replace: true });
  }, [user, onboardingDone, navigate]);

  // Pre-fill name from Google
  useEffect(() => {
    if (user?.user_metadata?.full_name && !data.full_name) {
      setData(prev => ({ ...prev, full_name: user.user_metadata.full_name }));
    }
  }, [user]);

  const update = (key, value) => setData(prev => ({ ...prev, [key]: value }));

  const uploadFotoProyecto = async (file) => {
    if (!user || !file) return;
    setUploadingFoto(true);
    const compressed = await compressImage(file);
    const ext = compressed.name.split('.').pop();
    const path = `${user.id}/foto_proyecto_${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from('avatars').upload(path, compressed, { upsert: true });
    if (upErr) { console.error('Upload error:', upErr.message); setUploadingFoto(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
    update('foto_proyecto', publicUrl);
    setUploadingFoto(false);
  };

  const next = () => setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const selectedInteresesCount = Object.values(data.intereses).filter(Boolean).length;

  const canAdvance = () => {
    switch (step) {
      case 0: return true; // intro
      case 1: return true; // bienvenida
      case 2: return data.full_name.trim().length > 0;
      case 3: return data.location.countryId !== null;
      case 4: return data.business_name.trim().length > 0;
      case 5: return data.identidad !== ''; // thinker o doer
      case 6: return data.business_stage !== '';
      case 7: return selectedInteresesCount >= 5; // Pinterest interests — mínimo 5
      case 8: return data.business_type !== '' && data.sales_type !== '';
      case 9: return Object.values(data.skills).some(v => v) && Object.values(data.needs).some(v => v);
      case 10: return data.presentacion_descripcion.trim().length > 0; // cuéntanos de ti
      case 11: return true; // presentación pública
      case 12: return data.primerPensamiento_titulo.trim().length > 0 && data.primerPensamiento_contenido.trim().length > 0;
      default: return true;
    }
  };

  const saveProfile = async () => {
    if (!user || saving) return;
    setSaving(true);

    const selectedSkills = Object.keys(data.skills).filter(k => data.skills[k]);
    const selectedNeeds = Object.keys(data.needs).filter(k => data.needs[k]);

    const updates = {
      id: user.id,
      email: user.email,
      full_name: data.full_name,
      avatar_url: user.user_metadata?.avatar_url || null,
      business_name: data.business_name || null,
      business_type: data.business_type || null,
      business_stage: data.business_stage || null,
      sales_type: data.sales_type || null,
      skill: selectedSkills.length > 0 ? selectedSkills.join(', ') : null,
      pain_point: selectedNeeds.length > 0 ? selectedNeeds.join(', ') : null,
      country_id: data.location.countryId || null,
      region_id: data.location.regionId || null,
      city_id: data.location.cityId || null,
      city: data.location.cityName || null,
      identidad: data.identidad || null,
      presentacion_descripcion: data.presentacion_descripcion || null,
      foto_proyecto: data.foto_proyecto || null,
      presentacion_quien: data.presentacion_quien || null,
      presentacion_que_hace: data.presentacion_que_hace || null,
      presentacion_que_busca: data.presentacion_que_busca || null,
      onboarding_done: true,
      profile_completed: true,
      membership: profile?.membership || 'free_observer',
      route: data.business_stage === 'idea' ? 'idea' : data.business_name ? 'negocio' : 'nada',
      updated_at: new Date().toISOString(),
    };

    console.log('[Onboarding] Saving profile:', updates);
    const { error } = await supabase.from('profiles').upsert(updates, { onConflict: 'id' });

    if (error) {
      console.error('[Onboarding] Error:', error.message);
      // Retry without identidad if column doesn't exist yet
      if (error.message?.includes('identidad')) {
        const { identidad, ...fallback } = updates;
        const { error: err2 } = await supabase.from('profiles').upsert(fallback, { onConflict: 'id' });
        if (err2) { console.error('[Onboarding] Fallback error:', err2.message); setSaving(false); return; }
      } else {
        setSaving(false);
        return;
      }
    }

    // Save interests to user_intereses table
    const selectedIntereses = Object.keys(data.intereses).filter(k => data.intereses[k]);
    if (selectedIntereses.length > 0) {
      await supabase.from('user_intereses').upsert(
        selectedIntereses.map(interes => ({ user_id: user.id, interes })),
        { onConflict: 'user_id,interes' }
      );
    }

    // Save first atomic thought
    if (data.primerPensamiento_titulo.trim() && data.primerPensamiento_contenido.trim()) {
      console.log('[Onboarding] Saving first thought:', { titulo: data.primerPensamiento_titulo, user_id: user.id });
      const { data: pensamiento, error: pensError } = await supabase.from('pensamientos').insert({
        user_id: user.id,
        titulo: data.primerPensamiento_titulo.trim(),
        contenido: data.primerPensamiento_contenido.trim(),
        visibilidad: 'publico',
      }).select().single();

      if (pensError) {
        console.error('[Onboarding] Error saving thought:', pensError.message, pensError);
      } else if (pensamiento) {
        console.log('[Onboarding] Thought saved:', pensamiento.id);
        const { error: sinError } = await supabase.from('sinapsis').insert({
          pensamiento_from_id: pensamiento.id,
          pensamiento_to_id: pensamiento.id,
          user_from_id: user.id,
          user_to_id: user.id,
          tipo: 'cluster',
        });
        if (sinError) console.error('[Onboarding] Error saving sinapsis:', sinError.message);
      }
    }

    await refreshProfile();
    setSaving(false);
    navigate('/sala');
  };

  // Shared styles
  const cardStyle = {
    background: t.card,
    border: `0.5px solid ${t.border}`,
    borderRadius: 14,
    padding: isMobile ? '1.5rem 1.2rem' : '2rem 1.5rem',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    animation: 'fadeIn 0.3s ease',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: 14,
    border: `0.5px solid ${t.border}`,
    borderRadius: 10,
    fontFamily: font,
    boxSizing: 'border-box',
    outline: 'none',
    background: t.inputBg,
    color: '#fff',
  };

  const btnPrimary = {
    width: '100%',
    padding: 14,
    fontSize: 15,
    fontWeight: 500,
    background: C.turquesa,
    color: 'white',
    border: 'none',
    borderRadius: 50,
    cursor: 'pointer',
    fontFamily: font,
    opacity: canAdvance() ? 1 : 0.4,
  };

  const optionStyle = (selected) => ({
    padding: '14px 16px',
    border: `0.5px solid ${selected ? C.turquesa : t.border}`,
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: selected ? 'rgba(93,193,185,0.06)' : 'transparent',
  });

  const chipStyle = (selected) => ({
    padding: '10px 16px',
    border: `0.5px solid ${selected ? C.turquesa : t.border}`,
    borderRadius: 50,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: 13,
    background: selected ? 'rgba(93,193,185,0.08)' : 'transparent',
    color: selected ? C.turquesa : 'rgba(255,255,255,0.7)',
    display: 'inline-block',
  });

  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  const renderStep = () => {
    switch (step) {
      // PASO 0: Intro
      case 0:
        return (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: C.logoGrad, margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#fff', fontSize: 20, fontWeight: 600 }}>T&D</span>
            </div>
            <p style={{ fontFamily: serif, fontSize: 24, fontWeight: 400, margin: '0 0 12px' }}>Tu cerebro emprendedor en red</p>
            <p style={{ fontSize: 14, color: C.gray, lineHeight: 1.7, margin: '0 0 8px' }}>
              Vamos a configurar tu perfil para conectarte con emprendedores que piensan como tú.
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', margin: 0 }}>12 pasos · 3-4 minutos</p>
          </div>
        );

      // PASO 1: Bienvenida personal
      case 1:
        return (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <p style={{ fontSize: 48, margin: '0 0 16px' }}>👋</p>
            <p style={{ fontFamily: serif, fontSize: 22, fontWeight: 400, margin: '0 0 12px' }}>
              Bienvenido a Thinker & Doer
            </p>
            <p style={{ fontSize: 14, color: C.gray, lineHeight: 1.7, margin: 0 }}>
              Aquí no hay likes, no hay followers, no hay vanidad. Solo emprendedores reales conectados por lo que piensan y hacen. Tu nombre real, tu proyecto real.
            </p>
          </div>
        );

      // PASO 2: Nombre
      case 2:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>¿Cómo te llamas?</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 14px' }}>Tu nombre real. Así te verán los demás emprendedores.</p>
            <input
              value={data.full_name}
              onChange={e => update('full_name', e.target.value)}
              placeholder="Tu nombre completo"
              style={inputStyle}
              autoFocus
            />
          </div>
        );

      // PASO 3: Ubicación
      case 3:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>¿Desde dónde emprendes?</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 14px' }}>Te conectamos con emprendedores cercanos a ti.</p>
            <LocationCascade
              value={data.location}
              onChange={loc => update('location', loc)}
              isMobile={isMobile}
            />
          </div>
        );

      // PASO 4: Proyecto
      case 4:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>¿Cómo se llama tu proyecto o negocio?</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 14px' }}>Si todavía no tiene nombre, ponle uno temporal. Lo puedes cambiar después.</p>
            <input
              value={data.business_name}
              onChange={e => update('business_name', e.target.value)}
              placeholder="Ej: Café Don Pedro, Mi Tienda Online..."
              style={inputStyle}
              autoFocus
            />
          </div>
        );

      // PASO 5: Identidad Thinker / Doer
      case 5: {
        const identidadCard = (tipo, emoji, color, titulo, desc) => {
          const sel = data.identidad === tipo;
          return (
            <div onClick={() => update('identidad', tipo)} style={{
              flex: 1, padding: isMobile ? '24px 16px' : '32px 24px', borderRadius: 14,
              border: `1.5px solid ${sel ? color : 'rgba(255,255,255,0.1)'}`,
              background: sel ? `${color}12` : 'rgba(255,255,255,0.03)',
              cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
              boxShadow: sel ? `0 0 24px ${color}20` : 'none',
            }}>
              <span style={{ fontSize: 36, display: 'block', marginBottom: 12 }}>{emoji}</span>
              <p style={{ fontFamily: serif, fontSize: 18, fontWeight: 400, margin: '0 0 8px', color: sel ? color : '#fff' }}>{titulo}</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 }}>{desc}</p>
            </div>
          );
        };
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>¿Eres Thinker o Doer?</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 18px' }}>Elige tu identidad dominante. Esto nos ayuda a conectarte con tu complemento.</p>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
              {identidadCard('thinker', '🧠', '#FDBE02', 'Thinker', 'Pienso, ideo, conecto. Mi fortaleza es ver patrones, generar ideas y conectar puntos. Necesito a alguien que las ejecute conmigo.')}
              {identidadCard('doer', '⚡', '#5dc1b9', 'Doer', 'Ejecuto, construyo, lanzo. Mi fortaleza es hacer que las cosas pasen. Necesito ideas frescas para construirlas.')}
            </div>
          </div>
        );
      }

      // PASO 6: Etapa
      case 6:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 14px' }}>¿En qué etapa está tu proyecto?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {STAGES.map(s => (
                <div key={s.value} onClick={() => update('business_stage', s.value)} style={optionStyle(data.business_stage === s.value)}>
                  <span style={{ fontSize: 22 }}>{s.emoji}</span>
                  <span style={{ fontSize: 14 }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        );

      // PASO 7: Pinterest de intereses
      case 7:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>¿Qué te apasiona?</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 6px' }}>Elige al menos 5 temas. Te ayudaremos a encontrar gente afín.</p>
            <p style={{ fontSize: 12, color: selectedInteresesCount >= 5 ? C.turquesa : C.coral, margin: '0 0 14px', fontWeight: 500 }}>
              Has elegido {selectedInteresesCount} de 15 máximo
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : isTablet ? 'repeat(4, 1fr)' : 'repeat(5, 1fr)', gap: 10 }}>
              {INTERESES.map(item => {
                const sel = !!data.intereses[item.id];
                const atMax = selectedInteresesCount >= 15 && !sel;
                return (
                  <div key={item.id} onClick={() => {
                    if (atMax) return;
                    update('intereses', { ...data.intereses, [item.id]: !sel });
                  }} style={{
                    background: sel ? 'rgba(93,193,185,0.15)' : 'rgba(255,255,255,0.04)',
                    border: sel ? '1px solid #5dc1b9' : '0.5px solid rgba(255,255,255,0.1)',
                    borderRadius: 10, padding: '16px 8px', textAlign: 'center', cursor: atMax ? 'not-allowed' : 'pointer',
                    minHeight: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                    transition: 'all 0.15s', opacity: atMax ? 0.4 : 1,
                  }}>
                    <span style={{ fontSize: 24 }}>{item.emoji}</span>
                    <p style={{ fontSize: 12, margin: 0, color: sel ? C.turquesa : 'rgba(255,255,255,0.85)' }}>{item.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );

      // PASO 8: Industria + Modelo
      case 8:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 14px' }}>¿De qué es tu proyecto?</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {INDUSTRIES.map(ind => (
                <div key={ind.value} onClick={() => update('business_type', ind.value)} style={chipStyle(data.business_type === ind.value)}>
                  {ind.emoji} {ind.label}
                </div>
              ))}
            </div>

            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 14px' }}>¿Cuál es tu modelo?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {MODELS.map(m => (
                <div key={m.value} onClick={() => update('sales_type', m.value)} style={optionStyle(data.sales_type === m.value)}>
                  <span style={{ fontSize: 22 }}>{m.emoji}</span>
                  <span style={{ fontSize: 14 }}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        );

      // PASO 9: Aportes + Necesidades
      case 9:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>¿En qué eres bueno?</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 14px' }}>Selecciona tus fortalezas. Esto alimenta tu neurona en la red.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {SKILLS_OPTIONS.map(s => (
                <div key={s.value} onClick={() => update('skills', { ...data.skills, [s.value]: !data.skills[s.value] })} style={chipStyle(data.skills[s.value])}>
                  {s.label}
                </div>
              ))}
            </div>

            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>¿Qué necesitas hoy?</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 14px' }}>Selecciona lo que más te urge. Te conectamos con quien puede ayudarte.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {NEEDS_OPTIONS.map(n => (
                <div key={n.value} onClick={() => update('needs', { ...data.needs, [n.value]: !data.needs[n.value] })} style={chipStyle(data.needs[n.value])}>
                  {n.label}
                </div>
              ))}
            </div>
          </div>
        );

      // PASO 10: Cuéntanos de ti o de tu proyecto
      case 10:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>Cuéntanos de ti o de tu proyecto</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 18px', lineHeight: 1.6 }}>
              Esto va a aparecer cuando alguien toque tu neurona o entre a Conoce Proyectos. Hazlo memorable: ¿qué te mueve? ¿qué construyes?
            </p>

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', margin: '0 0 6px' }}>Descripción</label>
            <textarea
              value={data.presentacion_descripcion}
              onChange={e => { if (e.target.value.length <= 300) update('presentacion_descripcion', e.target.value); }}
              placeholder="Soy diseñadora gráfica con una marca de velas artesanales. Me mueve crear cosas bonitas que hagan sentir bien a la gente."
              style={{ ...inputStyle, minHeight: 100, resize: 'vertical', marginBottom: 4 }}
            />
            <p style={{ fontSize: 11, color: data.presentacion_descripcion.length >= 280 ? C.coral : 'rgba(255,255,255,0.3)', margin: '0 0 18px', textAlign: 'right' }}>
              {data.presentacion_descripcion.length}/300
            </p>

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', margin: '0 0 6px' }}>Foto de tu proyecto o de ti</label>
            {data.foto_proyecto ? (
              <div style={{ marginBottom: 8 }}>
                <img src={data.foto_proyecto} alt="Foto proyecto" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 12, border: `0.5px solid ${t.border}` }} />
                <label style={{ fontSize: 12, color: C.turquesa, fontWeight: 500, cursor: 'pointer', display: 'block', marginTop: 6 }}>
                  Cambiar foto
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadFotoProyecto(e.target.files[0])} />
                </label>
              </div>
            ) : (
              <label style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '20px 16px', border: `1px dashed ${t.border}`, borderRadius: 12,
                cursor: 'pointer', marginBottom: 8, color: 'rgba(255,255,255,0.5)', fontSize: 13,
              }}>
                {uploadingFoto ? 'Subiendo...' : '📷 Subir foto'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadFotoProyecto(e.target.files[0])} />
              </label>
            )}
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: '4px 0 0' }}>
              La foto puedes subirla más tarde desde tu perfil
            </p>
          </div>
        );

      // PASO 11: Sobre ti — presentación pública
      case 11:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>Sobre ti</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 18px' }}>
              Esto es lo que verán los demás emprendedores cuando visiten tu perfil.
            </p>

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', margin: '0 0 6px' }}>¿Quién eres?</label>
            <input
              value={data.presentacion_quien}
              onChange={e => update('presentacion_quien', e.target.value)}
              placeholder="Ej: Soy mamá de dos, diseñadora gráfica de profesión y emprendedora por convicción."
              style={{ ...inputStyle, marginBottom: 16 }}
            />

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', margin: '0 0 6px' }}>¿Qué haces?</label>
            <input
              value={data.presentacion_que_hace}
              onChange={e => update('presentacion_que_hace', e.target.value)}
              placeholder="Ej: Tengo una marca de velas artesanales que vendo por Instagram."
              style={{ ...inputStyle, marginBottom: 16 }}
            />

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', margin: '0 0 6px' }}>¿Qué buscas aquí?</label>
            <input
              value={data.presentacion_que_busca}
              onChange={e => update('presentacion_que_busca', e.target.value)}
              placeholder="Ej: Quiero aprender a escalar mi negocio y encontrar gente con la misma energía."
              style={{ ...inputStyle, marginBottom: 8 }}
            />

            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: '8px 0 0' }}>
              Esto es público. Los demás emprendedores podrán verlo en tu perfil.
            </p>
          </div>
        );

      // PASO 12: Primer pensamiento atómico
      case 12:
        return (
          <div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>Escribe tu primer pensamiento atómico</p>
            <p style={{ fontSize: 12, color: C.gray, margin: '0 0 18px', lineHeight: 1.6 }}>
              Este aparecerá en la red neuronal pública. Piénsalo bien: es la primera idea de muchas. Empieza como un thinker, ejecútalo como un doer.
            </p>

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', margin: '0 0 6px' }}>Título</label>
            <input
              value={data.primerPensamiento_titulo}
              onChange={e => { if (e.target.value.length <= 60) update('primerPensamiento_titulo', e.target.value); }}
              placeholder="Postres saludables para oficinas"
              style={{ ...inputStyle, marginBottom: 4 }}
              autoFocus
            />
            <p style={{ fontSize: 11, color: data.primerPensamiento_titulo.length >= 50 ? C.coral : 'rgba(255,255,255,0.3)', margin: '0 0 16px', textAlign: 'right' }}>
              {data.primerPensamiento_titulo.length}/60
            </p>

            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', margin: '0 0 6px' }}>Contenido</label>
            <textarea
              value={data.primerPensamiento_contenido}
              onChange={e => { if (e.target.value.length <= 500) update('primerPensamiento_contenido', e.target.value); }}
              placeholder="Creo que hay una oportunidad enorme en llevar postres saludables a oficinas. La gente quiere comer bien pero no tiene tiempo..."
              style={{ ...inputStyle, minHeight: 120, resize: 'vertical', marginBottom: 4 }}
            />
            <p style={{ fontSize: 11, color: data.primerPensamiento_contenido.length >= 450 ? C.coral : 'rgba(255,255,255,0.3)', margin: '0 0 0', textAlign: 'right' }}>
              {data.primerPensamiento_contenido.length}/500
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const handleNext = () => {
    if (step === TOTAL_STEPS - 1) {
      saveProfile();
    } else if (canAdvance()) {
      next();
    }
  };

  return (
    <div style={{ minHeight: '100vh', maxHeight: '100vh', background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
    <div style={{ width: '100%', maxWidth: 720, maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', fontFamily: font, color: '#fff' }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: 16, flexShrink: 0 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: C.logoGrad, margin: '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'white', fontSize: 14, fontWeight: 600 }}>T&D</span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 3, marginBottom: 24, overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ height: '100%', width: `${progress}%`, background: C.turquesa, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </div>

      {/* Step counter */}
      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', textAlign: 'center', margin: '0 0 16px', flexShrink: 0 }}>
        Paso {step + 1} de {TOTAL_STEPS}
      </p>

      {/* Card */}
      <div style={{ ...cardStyle, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {renderStep()}
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, flexShrink: 0 }}>
        {step > 0 && (
          <button onClick={prev} style={{ flex: '0 0 auto', padding: '14px 20px', fontSize: 14, background: 'transparent', border: `0.5px solid ${t.border}`, borderRadius: 50, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontFamily: font }}>
            ← Atrás
          </button>
        )}
        <button
          className="td-glow-btn"
          onClick={handleNext}
          disabled={!canAdvance() || saving}
          style={{ ...btnPrimary, flex: 1, opacity: (canAdvance() && !saving) ? 1 : 0.4 }}
        >
          {saving ? 'Guardando...' : step === TOTAL_STEPS - 1 ? 'Entrar a la comunidad →' : 'Continuar'}
        </button>
      </div>

      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
    </div>
  );
}
